import { markRaw } from 'vue'
import {secondsDiff} from '@/utils/datetime'

const collection = (features) => markRaw({ type: 'FeatureCollection', features })
const featureSig = (f) => `${f.properties?.code}|${JSON.stringify(f.properties)}|${f.geometry ? JSON.stringify(f.geometry) : '-'}`
const thumbUrlOf = (resources) =>
  resources?.find(r => r.url?.includes('small'))?.url || resources?.find(r => r.url?.includes('thumbnail'))?.url

export const mutations = {
  SET_FEATURE_POPUP(state, { feature, coordinates }) {
    state.featurePopup = {
      feature,
      coordinates,
      counter: state.featurePopup.counter + 1,
      visible: true,
    }
  },

  CLEAR_FEATURE_POPUP(state) {
    state.featurePopup = {
      ...state.featurePopup,
      feature: null,
      coordinates: null,
      visible: false,
    }
  },

  SET_FEATURES(state, payload) {
    const newFeatures = payload.features.features.map(f => {
      const thumbUrl = thumbUrlOf(f.properties.resources)
      if (thumbUrl) {
        f.properties.thumbUrl = thumbUrl
      }
      return f
    })

    state.featureCache = markRaw({})
    state.featureCacheKey = null
    state.featureSigs = newFeatures.map(featureSig)
    state.features = collection(newFeatures)
  },

  MERGE_FEATURES(state, { features, cacheKey, reset = false }) {
    if (reset || cacheKey == null || cacheKey !== state.featureCacheKey) {
      state.featureCache = markRaw({})
      state.featureCacheKey = cacheKey ?? null
    }
    const cache = state.featureCache

    for (const f of features.features) {
      const thumbUrl = thumbUrlOf(f.properties.resources)
      if (thumbUrl) {
        f.properties.thumbUrl = thumbUrl
      }
      const code = f.properties.code
      if (f.properties.is_heavy && !f.geometry && state.loadedHeavyGeometries[code]) {
        f.geometry = state.loadedHeavyGeometries[code]
      }
      cache[code] = f
    }

    const newFeatures = Object.values(cache)
    const newSigs = newFeatures.map(featureSig)
    const oldSigs = state.featureSigs
    if (newSigs.length === oldSigs.length && newSigs.every((s, i) => s === oldSigs[i])) {
      return
    }

    state.featureSigs = newSigs
    state.features = collection(newFeatures)
  },

  INVALIDATE_FEATURE_FETCH(state) {
    state.featureFetchStamp++
  },

  SET_FEED_FEATURES(state, payload) {
    const { features } = payload
    const seen = new Set()
    const uniqueFeatures = features.features.filter(f => {
      const code = f.properties?.code
      if (!code || seen.has(code)) return false
      seen.add(code)
      return true
    })

    state.feedFeatures = collection(uniqueFeatures)
  },

  DELETE_FEATURE (state, code) {
    delete state.featureCache[code]
    const remaining = state.features.features.filter(f => f.properties.code !== code)
    state.featureSigs = remaining.map(featureSig)
    state.features = collection(remaining)
    state.feedFeatures = collection(state.feedFeatures.features.filter(f => f.properties.code !== code))
  },

  SET_CURRENT_FEATURE  (state, payload) {
    state.currentFeature = payload.feature
  },
  SET_CURRENT_FEATURES_CHILDS(state, payload) {
    state.currentFeatureChilds = payload
  },

  CLEAR_SEARCH_SELECTED(state) {
    state.searchSelected = []
  },

  SET_MAP(state, payload) {
    state.map = payload
  },
  SET_MAP_READY(state, payload) {
    state.mapReady = payload
  },
  SET_DRAW(state, payload) {
    state.draw = payload
  },

  SET_BEACONS(state, payload) {
    state.beacons = payload
  },
  NEXT_BEACON(state, next_beacon) {
    if (!state.beacons || state.beacons.find(b => b.properties.member_id === next_beacon.properties.member_id)) {
      const index = state.beacons.findIndex(b => b.properties.member_id === next_beacon.properties.member_id)
      state.beacons.splice(index, 1, next_beacon)
    } else {
      state.beacons.push(next_beacon)
    }
  },
  REMOVE_BEACON(state, memberId) {
    const index = state.beacons.findIndex(beacon => beacon.properties.member_id === memberId)
    state.beacons.splice(index, 1)
  },
  CLEANUP_BEACONS(state) {
    const day = 24 * 60 * 60 * 1000
    state.beacons = state.beacons.filter(beacon => secondsDiff(new Date(), beacon.properties.rel_ts) <= day)
    console.log('WS: cleanup beacons')
  },

  SET_LOCATION(state, location) {
    if(location === null) {
      state.userSettings.location.lat = null
      state.userSettings.location.lng = null
    } else {
      state.userSettings.location.lat = location.coords.latitude
      state.userSettings.location.lng = location.coords.longitude
    }
  },

  SET_LAYERS(state, payload) {
    state.layers = payload
  },

  SET_FILES (state, payload) {
    state.files = payload
  },

  SET_TOTAL_ONMAP(state, payload) {
    state.totalOnMap = payload
  },

  ADD_REPORT_TEMPLATE(state, payload) {
    state.reportTemplates.push(payload)
  },

  SET_REPORT_TEMPLATES(state, payload) {
    state.reportTemplates = payload
  },

  SET_ALL_TEMPLATES(state, payload) {
    state.allTemplates = payload
  },

  UPDATE_REPORT_TEMPLATE(state, payload) {
    const index = state.reportTemplates.findIndex(t => t.id === payload.id)
    state.reportTemplates.splice(index, 1, payload)
  },

  DELETE_REPORT_TEMPLATE(state, payload) {
    state.reportTemplates = state.reportTemplates.filter(t => t.id !== payload.id)
  },

  SET_SYSTEM_TEMPLATES_LIBRARY(state, payload) {
    state.systemTemplatesLibrary = payload
  },

  REMOVE_FROM_LIBRARY(state, templateId) {
    if (state.systemTemplatesLibrary) {
      state.systemTemplatesLibrary = state.systemTemplatesLibrary.filter(t => t.id !== templateId)
    }
  },

  ADD_TO_LIBRARY(state, template) {
    if (state.systemTemplatesLibrary) {
      state.systemTemplatesLibrary.push(template)
    } else {
      state.systemTemplatesLibrary = [template]
    }
  },

  SET_FEATURE_CREATED(state, payload) {
    state.isFeatureCreated = payload
  },

  TOGGLE_FEATURE_VISIBILITY(state, featureCode) {
    if (state.featureVisibility[featureCode] === undefined || state.featureVisibility[featureCode] === true) {
      // If not set or visible, hide it
      state.featureVisibility = { ...state.featureVisibility, [featureCode]: false }
    } else {
      // If hidden, show it
      state.featureVisibility = { ...state.featureVisibility, [featureCode]: true }
    }
  },

  SET_FEATURE_VISIBILITY(state, { featureCode, visible }) {
    state.featureVisibility = { ...state.featureVisibility, [featureCode]: visible }
  },

  RESET_FEATURE_VISIBILITY(state) {
    state.featureVisibility = {}
  },

  SET_RESOURCES(state, resources) {
    state.resources = resources
  },

  ADD_RESOURCE(state, resource) {
    if (!state.resources) {
      state.resources = []
    }
    state.resources.push(resource)
  },

  REMOVE_RESOURCE(state, resourceId) {
    if (state.resources) {
      state.resources = state.resources.filter(r => !(r.uuid === resourceId || r.id === resourceId))
    }
  },

  UPDATE_RESOURCE(state, { resourceId, updates }) {
    if (state.resources) {
      const index = state.resources.findIndex(r => r.uuid === resourceId || r.id === resourceId)
      if (index !== -1) {
        state.resources[index] = { ...state.resources[index], ...updates }
      }
    }
  },

  SET_HEAVY_FEATURE_GEOMETRY(state, { code, geometry }) {
    state.loadedHeavyGeometries = { ...state.loadedHeavyGeometries, [code]: geometry }

    if (state.featureCache[code]) {
      state.featureCache[code] = { ...state.featureCache[code], geometry }
    }
    const featureIndex = state.features.features.findIndex(f => f.properties.code === code)
    if (featureIndex !== -1) {
      const updatedFeatures = [...state.features.features]
      updatedFeatures[featureIndex] = { ...updatedFeatures[featureIndex], geometry }
      state.featureSigs = updatedFeatures.map(featureSig)
      state.features = collection(updatedFeatures)
    }
  },

  SET_HEAVY_FEATURE_LOADING(state, { code, loading }) {
    state.heavyFeatureLoading = { ...state.heavyFeatureLoading, [code]: loading }
  },

  CLEAR_HEAVY_GEOMETRIES(state) {
    state.loadedHeavyGeometries = {}
    state.heavyFeatureLoading = {}
  },

  SET_PROJECT_FILTERS(state, filters) {
    state.projectFilters = filters
  },
}
